import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  Filter,
  Plus,
  RefreshCw,
  ShieldAlert,
  Smartphone,
  XCircle,
} from 'lucide-react';

const API = axios.create({ baseURL: '/api/v1' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

interface SupportKbEntry {
  id: number;
  faqId: string | null;
  question: string;
  answer: string;
  category: string;
  source: string | null;
  reviewStatus: string | null;
  isActive: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  updatedAt: string;
  hallucinationFlags?: string[];
}

interface AuditSummary {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
  active: number;
  totalFlagged: number;
  breakdown: {
    appStore: number;
    staleKyc: number;
    easypayCashout: number;
    splitBill: number;
  };
}

const STATUS_CLASS: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border border-red-200',
};

const FLAG_LABELS: Record<string, { label: string; class: string }> = {
  app_store: { label: 'App Store Hallucination', class: 'bg-red-100 text-red-800 border-red-200' },
  stale_kyc: { label: 'Stale Driver Licence', class: 'bg-orange-100 text-orange-800 border-orange-200' },
  easypay_cashout: { label: 'EasyPay Cash-out', class: 'bg-purple-100 text-purple-800 border-purple-200' },
  split_bill: { label: 'QR Split Bill', class: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const CANONICAL_PWA_APP_QUESTION = 'Where do I find or download the MyMoolah app? / Is there an Android or iOS app?';
const CANONICAL_PWA_APP_ANSWER =
  'MyMoolah is a Progressive Web App (PWA) accessed directly in your mobile browser at https://wallet.mymoolah.africa (and via USSD at *120*5616#).\n\n' +
  'There is currently NO native app on the Google Play Store or Apple App Store.\n\n' +
  'You can install MyMoolah on your phone home screen for quick access:\n' +
  '• On Android (Chrome): Open https://wallet.mymoolah.africa, tap the three dots menu (⋮), and select "Add to Home screen" or "Install app".\n' +
  '• On iPhone (Safari): Open https://wallet.mymoolah.africa, tap the Share icon, and select "Add to Home Screen".\n\n' +
  'Never download APK files or apps from unverified links or third parties.';

const LIMIT = 25;

function errorMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ error?: string; message?: string }>;
  return axiosError.response?.data?.error || axiosError.response?.data?.message || fallback;
}

function listParams(
  page: number,
  filters: { reviewStatus: string; source: string; q: string; flagged: string },
  viewMode: 'actionable' | 'all'
) {
  const params: Record<string, string | number> = { page, limit: LIMIT };
  if (viewMode === 'actionable') {
    params.actionable = 'true';
  }
  if (filters.reviewStatus) params.reviewStatus = filters.reviewStatus;
  if (filters.source) params.source = filters.source;
  if (filters.q.trim()) params.q = filters.q.trim();
  if (filters.flagged) params.flagged = filters.flagged;
  return params;
}

export const SupportKnowledgeOverlay: React.FC = () => {
  const [viewMode, setViewMode] = useState<'actionable' | 'all'>('actionable');
  const [entries, setEntries] = useState<SupportKbEntry[]>([]);
  const [selected, setSelected] = useState<SupportKbEntry | null>(null);
  const [auditSummary, setAuditSummary] = useState<AuditSummary | null>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('admin');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useState({
    reviewStatus: '',
    source: '',
    q: '',
    flagged: '',
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / LIMIT)), [total]);

  const fetchAuditSummary = useCallback(async () => {
    try {
      const res = await API.get('/admin/support-kb/audit-summary');
      if (res.data?.success && res.data.data) {
        setAuditSummary(res.data.data);
      }
    } catch {
      // Non-critical background telemetry
    }
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await API.get('/admin/support-kb', {
        params: listParams(page, filters, viewMode),
      });
      setEntries(res.data.data.entries);
      setTotal(res.data.data.pagination.total);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to load Support KB entries'));
    } finally {
      setLoading(false);
    }
  }, [filters, page, viewMode]);

  useEffect(() => {
    fetchEntries();
    fetchAuditSummary();
  }, [fetchEntries, fetchAuditSummary]);

  const displayedEntries = entries;

  const actionableCount = useMemo(() => {
    return (auditSummary?.pending || 0) + (auditSummary?.totalFlagged || 0);
  }, [auditSummary]);

  const loadDetail = (entry: SupportKbEntry) => {
    setSelected(entry);
    setQuestion(entry.question);
    setAnswer(entry.answer);
    setCategory(entry.category || 'admin');
    setShowCreate(false);
    setNotice(null);
  };

  const updateFilter = (key: keyof typeof filters, value: string) => {
    setPage(1);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const applyCanonicalPwaAnswer = () => {
    setQuestion(CANONICAL_PWA_APP_QUESTION);
    setAnswer(CANONICAL_PWA_APP_ANSWER);
    setCategory('wallet');
    setNotice('Replaced with canonical PWA answer. Click "Save edits" to save.');
    setTimeout(() => setNotice(null), 4000);
  };

  const saveEdits = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await API.patch(`/admin/support-kb/${selected.id}`, { question, answer, category });
      const updated = res.data.data;
      if (
        viewMode === 'actionable' &&
        updated.reviewStatus === 'approved' &&
        (!updated.hallucinationFlags || updated.hallucinationFlags.length === 0)
      ) {
        setSelected(null);
        setNotice('Saved! Approved entry removed from review queue.');
      } else {
        setSelected(updated);
        setNotice('Saved successfully.');
      }
      await fetchEntries();
      await fetchAuditSummary();
      setTimeout(() => setNotice(null), 3500);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to save Support KB entry'));
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (action: 'approve' | 'reject') => {
    if (!selected) return;
    const reason = action === 'reject' ? window.prompt('Reason for reject?') : undefined;
    if (action === 'reject' && !reason) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await API.post(`/admin/support-kb/${selected.id}/${action}`, reason ? { reason } : {});
      setSelected(null);
      await fetchEntries();
      await fetchAuditSummary();
      setNotice(
        action === 'approve'
          ? 'Approved! Entry removed from review queue.'
          : 'Draft rejected and removed from review queue.'
      );
      setTimeout(() => setNotice(null), 3500);
    } catch (err: unknown) {
      setError(errorMessage(err, `Failed to ${action} Support KB entry`));
    } finally {
      setSaving(false);
    }
  };

  const createOfficial = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await API.post('/admin/support-kb', { question, answer, category });
      setShowCreate(false);
      loadDetail(res.data.data);
      await fetchEntries();
      await fetchAuditSummary();
      setNotice('Official Support KB answer created and published.');
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to create Support KB entry'));
    } finally {
      setSaving(false);
    }
  };

  const startCreate = () => {
    setSelected(null);
    setShowCreate(true);
    setQuestion('');
    setAnswer('');
    setCategory('admin');
    setNotice(null);
  };

  const isPwaCanonical =
    answer.includes('wallet.mymoolah.africa') &&
    (answer.includes('Progressive Web App') || answer.includes('PWA') || answer.toLowerCase().includes('progressive web app')) &&
    (answer.includes('NO native app') || answer.includes('no native app') || answer.toLowerCase().includes('no native app'));

  const hasAppStoreFlag =
    !isPwaCanonical &&
    (selected?.hallucinationFlags?.includes('app_store') ||
      (/\b(play\s*store|app\s*store|google\s*play|apple\s*store|apk)\b/i.test(`${question} ${answer}`) &&
        !/\b(no native app|not on the google play store|progressive web app|pwa|be suspicious)\b/i.test(`${question} ${answer}`)));

  const hasStaleKycFlag = selected?.hallucinationFlags?.includes('stale_kyc');
  const hasEasypayFlag = selected?.hallucinationFlags?.includes('easypay_cashout');
  const hasSplitBillFlag = selected?.hallucinationFlags?.includes('split_bill');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mymoolah-card flex flex-col gap-4 p-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-[var(--primary)]" />
            <h1 className="text-2xl font-semibold text-[var(--foreground)]">Support Knowledge Base</h1>
          </div>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Review overnight customer chat drafts, monitor hallucination alerts, and approve official answers.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={startCreate}
            className="inline-flex items-center gap-2 rounded-xl border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            Add official answer
          </button>
          <button
            type="button"
            onClick={() => {
              fetchEntries();
              fetchAuditSummary();
            }}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex items-center gap-3 border-b border-gray-200">
        <button
          type="button"
          onClick={() => {
            setViewMode('actionable');
            setSelected(null);
            setFilters({ reviewStatus: '', source: '', q: '', flagged: '' });
            setPage(1);
          }}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-all ${
            viewMode === 'actionable'
              ? 'border-gray-900 text-gray-900 font-bold'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <AlertTriangle className="h-4 w-4" />
          Action Required
          {actionableCount > 0 ? (
            <span className="rounded-full bg-amber-100 text-amber-800 text-xs px-2.5 py-0.5 font-bold">
              {actionableCount}
            </span>
          ) : (
            <span className="rounded-full bg-emerald-100 text-emerald-800 text-xs px-2.5 py-0.5 font-bold flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              0
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            setViewMode('all');
            setSelected(null);
            setFilters({ reviewStatus: '', source: '', q: '', flagged: '' });
            setPage(1);
          }}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-all ${
            viewMode === 'all'
              ? 'border-gray-900 text-gray-900 font-bold'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <BookOpen className="h-4 w-4" />
          All Knowledge Base
          <span className="rounded-full bg-gray-100 text-gray-600 text-xs px-2.5 py-0.5 font-bold">
            {auditSummary?.total || total}
          </span>
        </button>
      </div>

      {/* KPI / Audit Summary Cards - interactive across both views */}
      {auditSummary && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div
            onClick={() => {
              setViewMode('all');
              setSelected(null);
              setFilters({ reviewStatus: '', source: '', q: '', flagged: '' });
              setPage(1);
            }}
            className={`mymoolah-card cursor-pointer p-4 transition-all hover:border-[var(--primary)] ${
              viewMode === 'all' && !filters.reviewStatus && !filters.flagged ? 'ring-2 ring-[var(--primary)]' : ''
            }`}
          >
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Entries</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{auditSummary.total}</p>
            <p className="mt-1 text-xs text-emerald-600">{auditSummary.active} active in DB</p>
          </div>

          <div
            onClick={() => {
              setViewMode('all');
              setSelected(null);
              setFilters({ reviewStatus: 'approved', source: '', q: '', flagged: '' });
              setPage(1);
            }}
            className={`mymoolah-card cursor-pointer p-4 transition-all hover:border-emerald-500 ${
              viewMode === 'all' && filters.reviewStatus === 'approved' ? 'ring-2 ring-emerald-500' : ''
            }`}
          >
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Official Approved</p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">{auditSummary.approved}</p>
            <p className="mt-1 text-xs text-gray-500">Live authoritative answers</p>
          </div>

          <div
            onClick={() => {
              setViewMode('actionable');
              setSelected(null);
              setFilters({ reviewStatus: 'pending', source: '', q: '', flagged: '' });
              setPage(1);
            }}
            className={`mymoolah-card cursor-pointer p-4 transition-all hover:border-amber-500 ${
              viewMode === 'actionable' && filters.reviewStatus === 'pending' ? 'ring-2 ring-amber-500' : ''
            }`}
          >
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Overnight Drafts</p>
            <p className="mt-1 text-2xl font-bold text-amber-600">{auditSummary.pending}</p>
            <p className="mt-1 text-xs text-amber-700">Awaiting executive review</p>
          </div>

          <div
            onClick={() => {
              setViewMode('actionable');
              setSelected(null);
              setFilters({ reviewStatus: '', source: '', q: '', flagged: 'all_hallucinations' });
              setPage(1);
            }}
            className={`mymoolah-card cursor-pointer p-4 transition-all hover:border-red-500 ${
              viewMode === 'actionable' && filters.flagged ? 'ring-2 ring-red-500 bg-red-50/40' : ''
            } ${auditSummary.totalFlagged > 0 ? 'border-red-200' : ''}`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Quality Flags</p>
              {auditSummary.totalFlagged > 0 && <ShieldAlert className="h-4 w-4 text-red-600" />}
            </div>
            <p className={`mt-1 text-2xl font-bold ${auditSummary.totalFlagged > 0 ? 'text-red-700' : 'text-gray-900'}`}>
              {auditSummary.totalFlagged}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {auditSummary.breakdown.appStore > 0 && `${auditSummary.breakdown.appStore} app store · `}
              {auditSummary.breakdown.staleKyc > 0 && `${auditSummary.breakdown.staleKyc} stale KYC · `}
              {auditSummary.totalFlagged === 0 ? 'No hallucinations detected' : 'Click to filter'}
            </p>
          </div>
        </div>
      )}

      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
      {notice && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      {/* Actionable Sub-Filter Indicator */}
      {viewMode === 'actionable' && (filters.reviewStatus || filters.flagged || filters.q) && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-900">
          <div className="flex items-center gap-2">
            <Filter className="h-3.5 w-3.5 text-amber-700" />
            <span>
              Filtering Action Required by:{' '}
              <strong className="font-semibold">
                {filters.reviewStatus === 'pending'
                  ? 'Overnight Drafts only'
                  : filters.flagged
                  ? filters.flagged.replace('_', ' ')
                  : filters.q
                  ? `Search "${filters.q}"`
                  : ''}
              </strong>
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setFilters({ reviewStatus: '', source: '', q: '', flagged: '' });
              setPage(1);
            }}
            className="text-amber-800 hover:text-amber-950 font-bold underline"
          >
            Show all {actionableCount} actionable items
          </button>
        </div>
      )}

      {/* Filters Bar - only displayed in Full Library mode */}
      {viewMode === 'all' && (
        <div className="mymoolah-card p-4">
          <div className="mb-3 flex items-center justify-between text-sm font-semibold text-gray-700">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              Filters & Quality Scanners
            </div>
            {filters.flagged && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800">
                Filtering by: {filters.flagged.replace('_', ' ')}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <input
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Search question, answer, or FAQ ID"
              value={filters.q}
              onChange={(e) => updateFilter('q', e.target.value)}
            />
            <select
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              value={filters.reviewStatus}
              onChange={(e) => updateFilter('reviewStatus', e.target.value)}
            >
              <option value="">All review states</option>
              <option value="pending">Pending review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              value={filters.source}
              onChange={(e) => updateFilter('source', e.target.value)}
            >
              <option value="">All sources</option>
              <option value="auto_learn">Overnight chat draft (auto_learn)</option>
              <option value="admin">Admin portal added</option>
              <option value="seed">Seed FAQ</option>
            </select>
            <select
              className={`rounded-lg border px-3 py-2 text-sm focus:outline-none ${
                filters.flagged ? 'border-red-400 bg-red-50 text-red-900 font-medium' : 'border-gray-200'
              }`}
              value={filters.flagged}
              onChange={(e) => updateFilter('flagged', e.target.value)}
            >
              <option value="">All entries (no flag filter)</option>
              <option value="all_hallucinations">⚠️ All Flagged Hallucinations</option>
              <option value="app_store">📱 Native App Store mentions</option>
              <option value="stale_kyc">🪪 Stale Driver's Licence</option>
              <option value="easypay_cashout">💵 EasyPay Cash-out claims</option>
              <option value="split_bill">🧾 QR Split Bill claims</option>
            </select>
          </div>
        </div>
      )}

      {/* Main Content Layout */}
      {viewMode === 'actionable' && actionableCount === 0 && !loading ? (
        <div className="mymoolah-card p-12 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mb-3">
            <CheckCircle className="h-8 w-8" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">All updates are done!</h3>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
            Zero items require editing or approval. Every question in the knowledge base is verified and live.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={() => {
                setViewMode('all');
                setSelected(null);
                setFilters({ reviewStatus: '', source: '', q: '', flagged: '' });
                setPage(1);
              }}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Browse All Knowledge Base ({auditSummary?.total || total} entries)
            </button>
            <button
              onClick={startCreate}
              className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black transition-colors"
            >
              + Add Official Answer
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_440px]">
          {/* Table list */}
          <div className="mymoolah-card overflow-hidden">
            {loading && displayedEntries.length === 0 ? (
              <div className="p-8 text-center text-gray-400">Loading Support KB...</div>
            ) : displayedEntries.length === 0 ? (
              <div className="p-10 text-center text-sm text-gray-500">
                {viewMode === 'actionable'
                  ? 'No actionable items match the current filter.'
                  : 'No entries match the current filters.'}
                {(filters.flagged || filters.reviewStatus || filters.q || filters.source) && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({ reviewStatus: '', source: '', q: '', flagged: '' });
                      setPage(1);
                    }}
                    className="mt-2 block mx-auto text-blue-600 font-semibold hover:underline"
                  >
                    Clear filter
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50 text-left">
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Question & Intent</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Source</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Review</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Quality Alerts</th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {displayedEntries.map((entry) => {
                      const flags = entry.hallucinationFlags || [];
                      return (
                        <tr
                          key={entry.id}
                          onClick={() => loadDetail(entry)}
                          className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                            selected?.id === entry.id ? 'bg-blue-50/60' : ''
                          }`}
                        >
                          <td className="max-w-[360px] truncate px-4 py-3 font-medium text-gray-800">
                            <div className="truncate font-medium">{entry.question}</div>
                            <div className="truncate text-xs text-gray-400">{entry.answer.slice(0, 90)}...</div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">{entry.source || '—'}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                STATUS_CLASS[entry.reviewStatus || ''] || 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {entry.reviewStatus || 'unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {flags.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {flags.map((flag) => {
                                  const conf = FLAG_LABELS[flag] || { label: flag, class: 'bg-red-50 text-red-700' };
                                  return (
                                    <span
                                      key={flag}
                                      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold border ${conf.class}`}
                                    >
                                      <AlertTriangle className="h-3 w-3" />
                                      {conf.label}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {new Date(entry.updatedAt).toLocaleDateString('en-ZA')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-500">
              <span>
                Page {page} of {totalPages} ({total} {viewMode === 'actionable' ? 'actionable items' : 'entries'})
              </span>
              <div className="flex gap-2">
                <button
                  className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40 hover:bg-gray-50"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <button
                  className="rounded-lg border border-gray-200 px-3 py-1.5 disabled:opacity-40 hover:bg-gray-50"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

        {/* Detail Panel */}
        <aside className="mymoolah-card min-h-[520px] p-5">
          {!selected && !showCreate ? (
            <div className="flex h-full min-h-[400px] flex-col items-center justify-center text-center text-sm text-gray-500">
              <BookOpen className="h-10 w-10 text-gray-300 mb-2" />
              <p className="font-medium text-gray-600">Select a knowledge entry to inspect or review</p>
              <p className="text-xs text-gray-400 mt-1 max-w-[260px]">
                Review overnight drafts, fix hallucinations, or edit official answers.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    {showCreate ? 'New Official KB Answer' : `Entry #${selected?.id}`}
                  </h2>
                  {selected && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs font-mono text-gray-500">{selected.faqId || 'NO-ID'}</span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-500">{selected.source || 'manual'}</span>
                      <span className="text-xs text-gray-400">·</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          STATUS_CLASS[selected.reviewStatus || ''] || 'bg-gray-100'
                        }`}
                      >
                        {selected.reviewStatus}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  onClick={() => {
                    setSelected(null);
                    setShowCreate(false);
                  }}
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              {/* Actionable Caution Banners - ONLY rendered if an actionable problem is detected */}
              {hasAppStoreFlag && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  <div className="flex items-center gap-1.5 font-bold text-amber-900">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Native App Store Mention Detected
                  </div>
                  <p className="mt-1 text-amber-800 leading-relaxed">
                    This answer mentions app stores or downloads. MyMoolah is a Progressive Web App (PWA) accessed at <strong>wallet.mymoolah.africa</strong>. There is no native app on Google Play or Apple App Store.
                  </p>
                  <button
                    type="button"
                    onClick={applyCanonicalPwaAnswer}
                    className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-800 transition-colors"
                  >
                    <Smartphone className="h-3.5 w-3.5" />
                    Replace with Canonical PWA Answer
                  </button>
                </div>
              )}

              {hasStaleKycFlag && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  <div className="flex items-center gap-1.5 font-bold text-amber-900">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Stale KYC Claim Detected
                  </div>
                  <p className="mt-1 text-amber-800 leading-relaxed">
                    This answer mentions driver licences or temporary IDs. Only green ID books, smart ID cards, and valid passports are accepted for KYC.
                  </p>
                </div>
              )}

              {hasEasypayFlag && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  <div className="flex items-center gap-1.5 font-bold text-amber-900">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    EasyPay Cash-Out Inaccuracy
                  </div>
                  <p className="mt-1 text-amber-800 leading-relaxed">
                    EasyPay V5 is cash-in only (adding funds). It does not support wallet cash withdrawals.
                  </p>
                </div>
              )}

              {hasSplitBillFlag && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  <div className="flex items-center gap-1.5 font-bold text-amber-900">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    QR Split Bill Inaccuracy
                  </div>
                  <p className="mt-1 text-amber-800 leading-relaxed">
                    QR scan-to-pay pays the full till amount; bill splitting is disabled.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Question / Intent</label>
                <textarea
                  className="h-20 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="The customer question or search intent"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Authoritative Answer</label>
                <textarea
                  className="h-44 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm leading-relaxed focus:border-blue-500 focus:outline-none"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Official, factual answer. No store app hallucinations."
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Category</label>
                <input
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="wallet, kyc_documents, payments, etc."
                />
              </div>

              {/* Drawer Inline Feedback */}
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
                  {error}
                </div>
              )}
              {notice && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span>{notice}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
                {showCreate ? (
                  <button
                    disabled={saving}
                    onClick={createOfficial}
                    className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-black"
                  >
                    {saving ? 'Creating...' : 'Create Official Answer'}
                  </button>
                ) : (
                  <>
                    {/* Approved entries: only action is Save edits */}
                    {selected?.reviewStatus === 'approved' && (
                      <button
                        disabled={saving}
                        onClick={saveEdits}
                        className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 hover:bg-black transition-colors"
                      >
                        {saving ? 'Saving...' : 'Save edits'}
                      </button>
                    )}

                    {/* Pending drafts: actions needed are Approve, Save edits, or Reject */}
                    {selected?.reviewStatus === 'pending' && (
                      <>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => runAction('approve')}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4" />
                          Approve & Publish
                        </button>
                        <button
                          disabled={saving}
                          onClick={saveEdits}
                          className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {saving ? 'Saving...' : 'Save edits'}
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => runAction('reject')}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 transition-colors"
                        >
                          <XCircle className="h-4 w-4" />
                          Reject Draft
                        </button>
                      </>
                    )}

                    {/* Rejected entries: can re-approve or save edits */}
                    {selected?.reviewStatus === 'rejected' && (
                      <>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => runAction('approve')}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4" />
                          Re-approve & Publish
                        </button>
                        <button
                          disabled={saving}
                          onClick={saveEdits}
                          className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {saving ? 'Saving...' : 'Save edits'}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </aside>
      </div>
    )}
  </div>
);
};
